import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import StatsBar from './StatsBar';
import ColumnSettings from './ColumnSettings';
import LanguageSwitcher from './LanguageSwitcher';

const STORAGE_HIGHLIGHTS = 'highlightedRows';
const STORAGE_COLUMN = 'selectedColumn';
const STORAGE_VISIBLE = 'visibleColumns';
const STORAGE_FILTER = 'selectedFilter';

export default function DataView({ data, columns, fileName, onReset }) {
  const { t } = useTranslation();
  const [highlighted, setHighlighted] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_HIGHLIGHTS);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  const [searchColumn, setSearchColumn] = useState(() => {
    return localStorage.getItem(STORAGE_COLUMN) || columns[0] || '';
  });

  const [filterMode, setFilterMode] = useState(() => {
    return localStorage.getItem(STORAGE_FILTER) || 'all';
  });

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_VISIBLE);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Filter to only include columns that exist in current data
        const valid = parsed.filter(c => columns.includes(c));
        return valid.length > 0 ? valid : columns;
      }
    } catch {}
    return columns;
  });

  const [showSettings, setShowSettings] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportStep, setExportStep] = useState('filter'); // 'filter' or 'format'
  const exportFilterRef = useRef('all');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusError, setStatusError] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const inputRef = useRef(null);
  const exportRef = useRef(null);

  // Save highlighted set to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_HIGHLIGHTS, JSON.stringify(Array.from(highlighted)));
  }, [highlighted]);

  useEffect(() => {
    localStorage.setItem(STORAGE_COLUMN, searchColumn);
  }, [searchColumn]);

  useEffect(() => {
    localStorage.setItem(STORAGE_FILTER, filterMode);
  }, [filterMode]);

  useEffect(() => {
    localStorage.setItem(STORAGE_VISIBLE, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Focus barcode input on mount
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // Close export menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const highlightedCount = useMemo(() => {
    return highlighted.size;
  }, [highlighted]);

  const filteredData = useMemo(() => {
    return data.map((row, idx) => ({ ...row, __idx: idx })).filter(row => {
      const isHL = highlighted.has(row.__idx);
      if (filterMode === 'onlyHighlighted') return isHL;
      if (filterMode === 'onlyNotHighlighted') return !isHL;
      return true;
    });
  }, [data, highlighted, filterMode]);

  const setStatus = useCallback((msg, isError = false) => {
    setStatusMessage(msg);
    setStatusError(isError);
  }, []);

  const handleSearch = useCallback((value) => {
    if (!value.trim()) return;
    const trimmed = value.trim();
    let found = false;
    const newHighlighted = new Set(highlighted);
    
    data.forEach((row, idx) => {
      if (String(row[searchColumn] || '') === trimmed) {
        newHighlighted.add(idx);
        found = true;
      }
    });

    if (!found) {
      setStatus(t('notFound', { value: trimmed }), true);
    } else {
      setHighlighted(newHighlighted);
      setStatus(t('marked', { value: trimmed }));
    }
    setSearchValue('');
  }, [highlighted, data, searchColumn, setStatus]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearch(e.target.value);
    }
  }, [handleSearch]);

  const clearHighlights = useCallback(() => {
    setHighlighted(new Set());
    setStatus(t('allHighlightsCleared'));
  }, [setStatus]);

  const handleReset = useCallback(() => {
    if (window.confirm(t('confirmDelete'))) {
      localStorage.removeItem('excelData');
      localStorage.removeItem('excelColumns');
      localStorage.removeItem('excelFileName');
      localStorage.removeItem(STORAGE_HIGHLIGHTS);
      localStorage.removeItem(STORAGE_COLUMN);
      localStorage.removeItem(STORAGE_VISIBLE);
      localStorage.removeItem(STORAGE_FILTER);
      onReset();
    }
  }, [onReset]);

  const handleFilterChange = useCallback((mode) => {
    setFilterMode(mode);
  }, []);

  const handleVisibleColumnsChange = useCallback((cols) => {
    setVisibleColumns(cols);
  }, []);

  const handleExport = useCallback((format) => {
    setShowExportMenu(false);
    setExportStep('filter');

    const chosenFilter = exportFilterRef.current;
    const activeCols = visibleColumns.filter(c => columns.includes(c));

    // Build data based on chosen export filter
    const sourceData = data.map((row, idx) => ({ ...row, __idx: idx })).filter(row => {
      const isHL = highlighted.has(row.__idx);
      if (chosenFilter === 'onlyHighlighted') return isHL;
      if (chosenFilter === 'onlyNotHighlighted') return !isHL;
      return true;
    });

    const exportRows = sourceData.map(row => {
      const obj = {};
      activeCols.forEach(col => {
        obj[col] = row[col] != null ? row[col] : '';
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');

    const baseName = fileName.replace(/\.[^.]+$/, '');
    let ext, bookType;
    switch (format) {
      case 'csv':
        ext = '.csv'; bookType = 'csv'; break;
      case 'xls':
        ext = '.xls'; bookType = 'biff8'; break;
      default:
        ext = '.xlsx'; bookType = 'xlsx'; break;
    }

    XLSX.writeFile(wb, baseName + '_export' + ext, { bookType });
    setStatus(t('exportSuccess', { count: exportRows.length, format: ext.toUpperCase().replace('.', '') }));
  }, [data, highlighted, visibleColumns, columns, fileName, setStatus, t]);

  const openExportMenu = useCallback(() => {
    setExportStep('filter');
    exportFilterRef.current = 'all';
    setShowExportMenu(prev => !prev);
  }, []);

  const selectExportFilter = useCallback((filter) => {
    exportFilterRef.current = filter;
    setExportStep('format');
  }, []);

  return (
    <div className="data-view">
      <StatsBar
        totalCount={data.length}
        highlightedCount={highlightedCount}
        filterMode={filterMode}
        onFilterChange={handleFilterChange}
      />

      <div className="toolbar">
        <div className="toolbar-left">
          <div className="toolbar-title">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <span className="file-name">{fileName}</span>
          </div>
          <button className="btn-danger-sm" onClick={handleReset}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            {t('deleteData')}
          </button>
        </div>

        <div className="toolbar-right">
          <div className="search-group">
            <div className="barcode-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <line x1="7" y1="7" x2="7" y2="17"/>
                <line x1="10" y1="7" x2="10" y2="17"/>
                <line x1="13" y1="7" x2="13" y2="17"/>
                <line x1="16" y1="7" x2="16" y2="13"/>
                <line x1="16" y1="15" x2="16" y2="17"/>
              </svg>
            </div>
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder={t('searchPlaceholder')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className="btn-clear-search" onClick={clearHighlights} title={t('clear')}>
              {t('clear')}
            </button>
          </div>

          <div className="select-group">
            <label>{t('columnLabel')}</label>
            <select
              value={searchColumn}
              onChange={(e) => setSearchColumn(e.target.value)}
            >
              {columns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
          </div>

          <div className="select-group">
            <label>{t('filterLabel')}</label>
            <select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
            >
              <option value="all">{t('filterAll')}</option>
              <option value="onlyHighlighted">{t('filterOnlyScanned')}</option>
              <option value="onlyNotHighlighted">{t('filterNotScanned')}</option>
            </select>
          </div>

          <div className="export-wrapper" ref={exportRef}>
            <button className="btn-export" onClick={openExportMenu} title={t('exportData')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span>{t('exportData')}</span>
              <svg className="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {showExportMenu && (
              <div className="export-menu">
                {exportStep === 'filter' ? (
                  <>
                    <div className="export-menu-header">{t('exportSelectFilter')}</div>
                    <button onClick={() => selectExportFilter('all')}>
                      <span className="export-icon">📋</span> {t('exportAll', { count: data.length })}
                    </button>
                    <button onClick={() => selectExportFilter('onlyHighlighted')}>
                      <span className="export-icon">🟡</span> {t('exportScanned')} ({highlightedCount})
                    </button>
                    <button onClick={() => selectExportFilter('onlyNotHighlighted')}>
                      <span className="export-icon">⚪</span> {t('exportNotScanned')} ({data.length - highlightedCount})
                    </button>
                  </>
                ) : (
                  <>
                    <div className="export-menu-header">
                      <button className="export-back" onClick={() => setExportStep('filter')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 18 9 12 15 6"/>
                        </svg>
                      </button>
                      {t('exportSelectFormat')}
                    </div>
                    <button onClick={() => handleExport('xlsx')}>
                      <span className="export-icon">📗</span> {t('exportXLSX')}
                    </button>
                    <button onClick={() => handleExport('xls')}>
                      <span className="export-icon">📘</span> {t('exportXLS')}
                    </button>
                    <button onClick={() => handleExport('csv')}>
                      <span className="export-icon">📄</span> {t('exportCSV')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <LanguageSwitcher />
          <button className="btn-settings" onClick={() => setShowSettings(true)} title={t('columnSettings')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th className="row-number">#</th>
              {visibleColumns.filter(c => columns.includes(c)).map(col => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, displayIdx) => (
              <tr key={row.__idx} className={highlighted.has(row.__idx) ? 'row-highlighted' : ''}>
                <td className="row-number">{row.__idx + 1}</td>
                {visibleColumns.filter(c => columns.includes(c)).map(col => (
                  <td key={col}>{row[col] != null ? String(row[col]) : ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {filteredData.length === 0 && (
          <div className="empty-table">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              <line x1="8" y1="11" x2="14" y2="11"/>
            </svg>
            <p>{t('noDataFound')}</p>
          </div>
        )}
      </div>

      <div className={`status-bar ${statusError ? 'status-error' : ''}`}>
        {statusMessage || t('rowsShowing', { filtered: filteredData.length, total: data.length })}
      </div>

      {showSettings && (
        <ColumnSettings
          allColumns={columns}
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={handleVisibleColumnsChange}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
